import type { ReactNode } from 'react'

import { cn } from '@gto/ui/cn'
import { useIsTextOverflowing } from '@gto/ui/hooks/use-is-text-overflowing'
import { Tooltip, type TooltipProps } from '@gto/ui/tooltip'
import { Typography, type TypographyProps } from '@gto/ui/typography'

type TruncatedTextBaseProps = Omit<TypographyProps, 'noWrap' | 'ref' | 'children'> & {
  /** Tooltip body shown while the text is clipped. Defaults to `children`. */
  tooltip?: ReactNode
  /** Which side the tooltip opens on. Defaults to the DS `top`. */
  placement?: TooltipProps['placement']
  /** Hover delay before the tooltip opens, in ms. Defaults to the DS delay. */
  delay?: TooltipProps['delay']
}

/**
 * `contentKey` is what the measurement re-runs on. A `ResizeObserver` sees BOX changes only,
 * so re-measuring when the TEXT changes at an unchanged width is the key's whole job.
 *
 * A string/number child is its own key for free. An ELEMENT child is a fresh object every
 * render, so defaulting to it would force a synchronous layout read on EVERY render — per
 * row, in the lists this component exists for. That reflow is invisible in review and in
 * jsdom, so the union below makes the key a type obligation for element children.
 */
export type TruncatedTextProps = TruncatedTextBaseProps &
  (
    | {
        /** The text to render. Doubles as the tooltip body unless `tooltip` overrides it. */
        children: string | number
        /** Defaults to `children`, which already identifies the text. */
        contentKey?: unknown
      }
    | {
        /** The text to render. Doubles as the tooltip body unless `tooltip` overrides it. */
        children: ReactNode
        /** Required: the value the rendered text derives from (a translation id, a row id). */
        contentKey: unknown
      }
  )

/**
 * One line of text that truncates with an ellipsis when it does not fit, and
 * reveals its full content in a tooltip on hover — the tooltip existing ONLY while
 * the text is actually clipped, so a cell that fits carries no phantom hover target.
 *
 * The element ref is owned by the overflow measurement and is not forwardable.
 */
export const TruncatedText = ({
  children,
  className,
  contentKey,
  delay,
  placement,
  tooltip,
  ...typographyProps
}: TruncatedTextProps) => {
  // Fall back to `children` ONLY where the type sanctions it — a primitive child IS a stable
  // key. Falling back for an element child (reachable at runtime by passing `contentKey`
  // explicitly `undefined`) would reinstate the per-render reflow the union exists to prevent;
  // measuring once and never re-measuring is the safe failure.
  const measurementKey =
    contentKey ?? (typeof children === 'string' || typeof children === 'number' ? children : null)
  const { ref, isOverflowing } = useIsTextOverflowing(measurementKey)

  const text = (
    <Typography
      ref={ref}
      noWrap
      className={cn(
        // `min-w-0` is the load-bearing half of the pair. As a FLEX ITEM (which is
        // what every table cell makes this) the default `min-width: auto` floors the
        // element at its own max-content width, so it never narrows, its own
        // `truncate` never has anything to trim, and the text instead spills and is
        // hard-clipped by the cell's `overflow-hidden` — cut mid-word, no ellipsis.
        // `block` is the other half: the inline-rendering variants (`overline`,
        // `caption`, …) would otherwise ignore `text-overflow` entirely.
        'block min-w-0',
        className,
      )}
      {...typographyProps}
    >
      {children}
    </Typography>
  )

  // Mounted only when clipped: this renders once per cell of a long list, and an
  // always-mounted Base UI tooltip root would cost a media-query subscription per
  // row for text that has nothing to reveal. Toggling the wrapper remounts the
  // span, which re-measures it — to the same verdict, since Base UI's trigger
  // CLONES the element rather than wrapping it, so the layout is unchanged.
  if (!isOverflowing) {
    return text
  }

  return (
    <Tooltip
      // The revealed text is whatever did not fit, so it carries no bound of its own and
      // would open as one viewport-wide line; `break-words` covers the unbroken token (an
      // id, a URL) a measure alone cannot wrap. Not overridable, unlike `HelpIconTooltip`'s
      // matching cap, because here `className` is the TEXT's — widening would need a
      // separate `tooltipClassName`, and a single clipped line fits 320px.
      className="max-w-80 break-words"
      content={tooltip ?? children}
      delay={delay}
      placement={placement}
    >
      {text}
    </Tooltip>
  )
}
